import { useCallback, useEffect, useState } from 'react';
import {
  warehouseApi,
  type WarehouseResponse,
  type WmDockAppointment,
} from '@/api/warehouse.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { DateTimePicker } from '@/components/ui/DateTimePicker';
import { FormGrid } from '@/components/ui/FormGrid';
import { Select } from '@/components/ui/Select';
import { usePermissions } from '@/hooks/usePermissions';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
export function WarehouseDockPage() {
  const { hasAny } = usePermissions();
  const canReceive = hasAny('WAREHOUSE_RECEIVE', 'WAREHOUSE_ADMIN');
  const [appointments, setAppointments] = useState<WmDockAppointment[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseResponse[]>([]);
  const [dockId, setDockId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [carrier, setCarrier] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');

  const reload = useCallback(() => {
    warehouseApi
      .listDockAppointments()
      .then(setAppointments)
      .catch((e) => showApiError(e, 'Failed to load dock appointments'));
  }, []);

  useEffect(() => {
    reload();
    warehouseApi.listWarehouses().then(setWarehouses).catch(() => {});
  }, [reload]);

  const schedule = async () => {
    if (!dockId || !warehouseId || !start || !end) return;
    try {
      await warehouseApi.scheduleDock({
        dockId: Number(dockId),
        warehouseId: Number(warehouseId),
        carrierName: carrier.trim() || undefined,
        scheduledStart: start.length === 16 ? `${start}:00` : start,
        scheduledEnd: end.length === 16 ? `${end}:00` : end,
      });
      notify.success('Dock appointment scheduled');
      reload();
    } catch (e) {
      showApiError(e, 'Schedule failed');
    }
  };

  return (
    <div className="pb-safe">
      <PageHeader
        title="Dock scheduling"
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Warehouse', to: '/warehouse' },
          { label: 'Dock' },
        ]}
        actions={<Button variant="secondary" onClick={reload}>Refresh</Button>}
      />

      <p className="mb-4 text-sm text-slate-600">
        Dock IDs reference physical dock doors configured in your warehouse master (use ID from setup).
      </p>

      <div className="card mb-6 p-4">
        <h2 className="mb-3 font-semibold">Schedule appointment</h2>
        {canReceive ? (
          <>
            <FormGrid columns={3}>
              <Input label="Dock ID" value={dockId} onChange={(e) => setDockId(e.target.value)} />
              <Select
                label="Warehouse"
                value={warehouseId}
                onChange={(v) => setWarehouseId(v)}
                options={[
                  { value: '', label: 'Select…' },
                  ...warehouses.map((w) => ({ value: String(w.id), label: w.code })),
                ]}
              />
              <Input label="Carrier" value={carrier} onChange={(e) => setCarrier(e.target.value)} />
              <DateTimePicker label="Start (local)" value={start} onChange={setStart} />
              <DateTimePicker label="End (local)" value={end} onChange={setEnd} />
            </FormGrid>
            <Button className="mt-3" onClick={() => void schedule()}>
              Schedule
            </Button>
          </>
        ) : (
          <p className="text-sm text-slate-500">Receiving permission required to schedule dock appointments.</p>
        )}
      </div>

      <ul className="space-y-2">
        {appointments.map((a) => (
          <li key={a.id} className="card flex flex-wrap items-center justify-between gap-2 p-3 text-sm">
            <div>
              Dock {a.dockId} · WH {a.warehouseId} · {a.carrierName ?? '—'} · {a.statusCode}
              <br />
              {a.scheduledStart} → {a.scheduledEnd}
            </div>
            {canReceive && a.statusCode === 'SCHEDULED' && (
              <Button
                className="text-sm py-1 px-2"
                onClick={() =>
                  void warehouseApi.checkInDock(a.id).then(() => {
                    notify.success('Checked in');
                    reload();
                  }).catch((e) => showApiError(e, 'Check-in failed'))
                }
              >
                Check in
              </Button>
            )}
          </li>
        ))}
      </ul>
      {appointments.length === 0 && <p className="text-sm text-slate-500">No dock appointments.</p>}
    </div>
  );
}
